/**
 * AvgCloud series builder (R114) — per-bar cloud state + fill edges, in the
 * shape the new RockBand evaluator consumes. Reuses the chart's own utils so
 * the values match the AvgCloud overlay exactly.
 *
 *   cloudState = +1 (green) when the avg O/C band centre is above the Combined
 *                Midline, −1 (red) when below, 0 otherwise.
 *   lowerEdge / upperEdge follow the AvgCloud fill:
 *     green → lower = Combined Midline, upper = avg O/C bottom
 *     red   → upper = Combined Midline, lower = avg O/C top
 */
import { CandleRow } from '../components/charts/TradingChart';
import { computeEnvelope, computeCombinedEnvelope } from './dailyBlastIndicators';
import { computeLRHamaBand } from './signalIndicators';

export interface CloudPoint {
  time: number;
  cloudState: number;            // +1 green, −1 red, 0 neutral/na
  lowerEdge: number | null;
  upperEdge: number | null;
}

/** Compute per-bar cloud state + edges keyed to `rawCandles` times. CTF / HTF
 *  drive the Combined Midline; pass empty arrays to fall back to the LTF-only
 *  envelope (the cloud will then be coarser but still computed). */
export function computeCloudSeries(
  rawCandles: CandleRow[],
  ctf: CandleRow[],
  htf: CandleRow[],
): CloudPoint[] {
  const out: CloudPoint[] = [];
  if (!rawCandles?.length) return out;

  const ltfEnv = computeEnvelope(rawCandles);
  const comb = computeCombinedEnvelope(rawCandles, ltfEnv, ctf, htf);
  const midByTime = new Map<number, number>();
  comb.times.forEach((t, i) => {
    const v = comb.mid[i];
    if (v != null && Number.isFinite(v)) midByTime.set(t, v);
  });
  const bandByTime = new Map<number, { top: number; bot: number }>();
  for (const b of computeLRHamaBand(rawCandles)) {
    bandByTime.set(b.time, { top: Math.max(b.upper, b.lower), bot: Math.min(b.upper, b.lower) });
  }

  for (const c of rawCandles) {
    const t = c.time;
    const mid = midByTime.get(t);
    const bd = bandByTime.get(t);
    if (mid == null || !bd) { out.push({ time: t, cloudState: 0, lowerEdge: null, upperEdge: null }); continue; }
    const center = (bd.top + bd.bot) / 2;
    const state = center > mid ? 1 : center < mid ? -1 : 0;
    const lowerEdge = state === 1 ? mid : state === -1 ? bd.top : null;
    const upperEdge = state === 1 ? bd.bot : state === -1 ? mid : null;
    out.push({ time: t, cloudState: state, lowerEdge, upperEdge });
  }
  return out;
}
